// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The application shell (spec §6.8).
 *
 * Layout: file loaders + regions + verification + change list on the left, the
 * rendered board in the middle, merge controls on the right.
 *
 * The verification result is the most important element on the page and is
 * rendered first, largest, and without qualification.
 *
 * Nothing in the diff, verification or merge path depends on the view: if the
 * renderer throws, the panels still render (spec §7 — "the renderer is the least
 * reliable component and nothing critical may depend on it").
 */

import { SexpError } from '../sexp/index.ts';
import { Board } from '../model/board.ts';
import { formatBBox } from '../model/bbox.ts';
import { diffBoards, describeChange, type DiffResult, type ObjectChange } from '../diff/diff.ts';
import { formatFieldChange, type FieldChange } from '../diff/fields.ts';
import {
  buildPlan,
  claimsByChange,
  computeEffects,
  describeEffects,
  GLOBAL_BLOCK_INFO,
  makeSelection,
  selectionContents,
  type BuiltPlan,
  type GlobalBlockId,
  type Rect,
  type Selection,
} from '../select/index.ts';
import { checkPlan, compareBoards, IncomparableFilesError, type BoardFact, type PlanCheck } from '../verify/index.ts';
import { merge, MergeAbort, OVERRIDE_CONFIRMATION, type MergeResult } from '../merge/merge.ts';
import { clear, h, makeDropTarget, mm, nextPaint, plural, saveFile, sectionScrollTarget, svgIcon } from './dom.ts';
import { BoardView, selectionColor, TINT_A, TINT_B, type ViewInput, type ViewMode, type ViewStats } from './view.ts';
import { layerLabel, layerStyle, UNSUPPORTED_LAYER } from '../render/palette.ts';
import {
  browserFetch,
  resolveRepo,
  RepoAccessError,
  type FetchLike,
  type GitRef,
  type Provider,
  type RepoInfo,
} from '../vcs/index.ts';

/** The mark on its own: header, favicon, dialog. */
const LOGO_MARK = 'logo.svg';
/** Mark plus wordmark: the splash, and the empty canvas. */
const LOGO_FULL = 'logo-full.svg';

const BRAND = {
  name: 'deltaKi',
  /** The wordmark as it appears in the interface. */
  short: 'ΔKi',
  tagline: 'PCB DIFF + MERGE',
  /** Sentence-case for the tab, where all-caps reads as shouting. Must match public/index.html. */
  titleTagline: 'PCB diff + merge',
  author: 'Igor Brkić',
  authorEmail: 'igor@hyperglitch.com',
  company: 'HYPERGLITCH Ltd',
  companyUrl: 'https://hyperglitch.com',
  // Neither of these exists yet; both are placeholders to be confirmed.
  docsUrl: 'https://hyperglitch.com/articles/kitools',
  repoUrl: 'https://gitlab.com/hyperglitch/deltaki',
} as const;

/** How long the splash stays up before fading, in milliseconds. */
const SPLASH_MS = 1000;

/** Zoom factor of one press of the +/− buttons. */
const ZOOM_STEP = 1.5;

/** Width below which the app starts with both sidebars folded. Matches styles.css. */
const NARROW_SCREEN = '(max-width: 700px)';

function narrowScreen(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_SCREEN).matches;
}

/** Header height, matching `--panel-head-h`. Only a fallback when unmeasurable. */
const PANEL_HEADER_PX = 33;

interface LoadedFile {
  name: string;
  text: string;
  board?: Board;
  error?: string;
}

export interface AppOptions {
  /**
   * Network access. Defaults to the locked-down browser fetch; injected by
   * tests so the repository flow can be exercised without a network.
   */
  fetch?: FetchLike;
  /** Show the opening splash. Off in tests, which have no use for a delay. */
  splash?: boolean;
}

export class App {
  private root: HTMLElement;
  private readonly fetchImpl: FetchLike;
  private view?: BoardView;

  private a?: LoadedFile;
  private b?: LoadedFile;
  /**
   * Merge mode. Browsing and committing are deliberately separate: nothing in
   * browse mode is one click away from producing a file.
   */
  private merging = false;
  /** The rectangles the user has drawn, in the order they drew them. */
  private selections: Selection[] = [];
  private activeSelectionId?: string;
  /** Board-level blocks to take from Board B. These have no position to draw. */
  private takeGlobal = new Set<GlobalBlockId>();
  private selectionCounter = 0;
  /** Selections the user has folded shut in the list. */
  private collapsedSelections = new Set<string>();

  /**
   * Board A as it was loaded, kept so a merge can be undone.
   *
   * Set only on the first merge, so reverting always returns to the file the
   * user opened rather than unwinding one merge at a time — a chain of partial
   * states is not something anyone can hold in their head while looking at a
   * board.
   */
  private originalA?: LoadedFile;

  private diff?: DiffResult;
  private facts: BoardFact[] = [];
  private plan?: BuiltPlan;
  private planCheck?: PlanCheck;
  private blockingError?: string;

  private mode: ViewMode = 'onion';
  private blend = 0.5;
  /** Divider position for the split view, as a fraction of the canvas width. */
  private splitFraction = 0.5;
  private tintBoards = true;
  private emphasiseChanges = false;
  /** Whether the user has had a say about {@link emphasiseChanges} yet. */
  private emphasiseSettled = false;
  private showOverlay = true;
  private visibleLayers = new Set<string>();
  private layersInitialised = false;
  private viewStats?: ViewStats;
  private showMetadata = false;
  /** Narrow the change list to what the selections claim. */
  private onlySelected = false;
  private selectedKey?: string;
  /** The selection the change list was last scrolled to, so it scrolls once. */
  private scrolledToKey?: string;
  /** Sidebars folded to a thin rail. A phone screen starts with the board, not the panels. */
  private leftCollapsed = narrowScreen();
  private rightCollapsed = narrowScreen();

  // --- source: local files, or a public git repository ------------------------
  private sourceMode: 'local' | 'git' = 'local';
  private repoUrl = '';
  private repo?: { provider: Provider; info: RepoInfo; refs: GitRef[]; files: string[] };
  private repoPath?: string;
  private repoCommits: GitRef[] = [];
  private repoRef: { a?: string; b?: string } = {};
  private repoBusy?: string;
  private repoError?: RepoAccessError;
  /** Downloaded files, so flipping between revisions does not re-fetch. */
  private repoCache = new Map<string, string>();

  /** What the tool is busy doing, shown as a blocking overlay. */
  private busy?: string;
  /**
   * Whether the current regions came from auto-detection.
   *
   * Auto-detected regions are re-derived whenever Board A changes, since they
   * describe *that* board's outlines. Hand-edited or imported ones are the
   * user's decision and are left alone.
   */
  private regionsAreAuto = false;

  private overrideAcknowledged = false;
  private overrideText = '';
  private mergeResult?: MergeResult;
  private mergeError?: MergeAbort;
  private saveNotice?: string;

  private els: {
    left: HTMLElement;
    main: HTMLElement;
    right: HTMLElement;
    canvas?: HTMLCanvasElement;
    toolbar?: HTMLElement;
    layerBar?: HTMLElement;
    footer?: HTMLElement;
    blendSlider?: HTMLInputElement;
    blendLabel?: HTMLElement;
    selectedRow?: HTMLElement;
    overlay?: HTMLElement;
    mergeButton?: HTMLButtonElement;
    watermark?: HTMLElement;
    mergedNotice?: HTMLElement;
    dialog?: HTMLElement;
  };

  constructor(root: HTMLElement, options: AppOptions = {}) {
    this.root = root;
    this.fetchImpl = options.fetch ?? browserFetch;
    const left = h('div', { class: 'col col-left' });
    const main = h('div', { class: 'col col-main' });
    const right = h('div', { class: 'col col-right' });
    const overlay = h(
      'div',
      { class: 'overlay' },
      h('div', { class: 'overlay-card' }, h('div', { class: 'spinner' }), h('div', { class: 'overlay-text' })),
    );
    this.els = { left, main, right, overlay };
    clear(root).append(left, main, right, overlay);

    this.buildMain();
    this.render();
    this.bindKeys();
    if (options.splash !== false) this.showSplash();
  }

  /**
   * A moment of the logo before the tool appears.
   *
   * Dismissible by click or key so it never stands between the user and the
   * work, and skipped entirely when the system asks for reduced motion.
   */
  private showSplash(): void {
    const splash = h(
      'div',
      { class: 'splash' },
      h('img', { src: LOGO_FULL, alt: `${BRAND.name} — ${BRAND.tagline}`, class: 'splash-logo' }),
    );
    this.root.append(splash);

    let done = false;
    const dismiss = (): void => {
      if (done) return;
      done = true;
      splash.classList.add('leaving');
      setTimeout(() => splash.remove(), 400);
    };
    splash.addEventListener('click', dismiss);
    globalThis.addEventListener?.('keydown', dismiss, { once: true });

    const reduced =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(dismiss, reduced ? 0 : SPLASH_MS);
  }

  // --- long operations ------------------------------------------------------------

  /**
   * Show the overlay, let the browser paint it, then do the work.
   *
   * The yield is the whole point. JavaScript is single-threaded, so setting a
   * flag and calling the work on the next line means the frame never reaches
   * the screen and the tab simply freezes — which is exactly what a 42 MB board
   * used to do. Waiting for two animation frames guarantees the overlay has
   * been painted before anything blocking starts.
   */
  private async busyRun<T>(message: string, work: () => T): Promise<T> {
    this.setBusy(message);
    await nextPaint();
    try {
      return work();
    } finally {
      this.setBusy(undefined);
    }
  }

  /** Update the overlay directly: a full re-render would itself block. */
  private setBusy(message?: string): void {
    this.busy = message;
    const overlay = this.els.overlay;
    if (!overlay) return;
    overlay.classList.toggle('on', message !== undefined);
    const text = overlay.querySelector('.overlay-text');
    if (text) text.textContent = message ?? '';
  }

  // --- data flow ---------------------------------------------------------------

  private async load(slot: 'a' | 'b', file: File): Promise<void> {
    const text = await file.text();
    await this.loadBoard(slot, file.name, text);
  }

  /**
   * Load a board with progress shown. Each phase is a separate paint so the
   * overlay says what is actually happening rather than just spinning.
   */
  async loadBoard(slot: 'a' | 'b', name: string, text: string): Promise<void> {
    const size = text.length > 2e6 ? ` (${(text.length / 1e6).toFixed(0)} MB)` : '';
    await this.busyRun(`Reading ${name}${size}…`, () => this.parseInto(slot, name, text));
    await this.busyRun('Comparing boards…', () => this.recomputeCore());
    await this.busyRun('Drawing…', () => this.render());
  }

  /**
   * Load a board from text. Separate from the File plumbing so the app can be
   * driven headlessly — the UI is tested without a browser, and §7 requires the
   * non-renderer parts to keep working when the renderer does not.
   */
  loadText(slot: 'a' | 'b', name: string, text: string): void {
    this.parseInto(slot, name, text);
    this.recompute();
  }

  /** Parse one board into a slot. No comparison, no rendering. */
  private parseInto(slot: 'a' | 'b', name: string, text: string): void {
    const loaded: LoadedFile = { name, text };
    try {
      loaded.board = Board.parse(text, name);
    } catch (err) {
      loaded.error =
        err instanceof SexpError
          ? err.message
          : `${name}: could not be read as a KiCad board — ${(err as Error).message}`;
    }
    this[slot] = loaded;
  }

  /**
   * Forget both boards and everything derived from them.
   *
   * Called when the source itself changes — a different repository, or a
   * different board file — because leaving the previous board on the canvas
   * with the previous regions beside it invites reading one board's verdict as
   * the other's.
   */
  private resetBoards(): void {
    this.a = undefined;
    this.b = undefined;
    this.originalA = undefined;
    this.merging = false;
    this.selections = [];
    this.activeSelectionId = undefined;
    this.takeGlobal = new Set();
    this.selectionCounter = 0;
    this.collapsedSelections = new Set();
    this.diff = undefined;
    this.facts = [];
    this.plan = undefined;
    this.planCheck = undefined;
    this.blockingError = undefined;
    this.mergeResult = undefined;
    this.mergeError = undefined;
    this.overrideAcknowledged = false;
    this.overrideText = '';
    this.saveNotice = undefined;
    this.selectedKey = undefined;
    this.scrolledToKey = undefined;
    this.viewStats = undefined;
    // Layer visibility is seeded from whatever board is loaded, so let the next
    // one seed it again rather than inheriting the previous stackup.
    this.layersInitialised = false;
    this.visibleLayers = new Set();
    this.emphasiseChanges = false;
    this.emphasiseSettled = false;
  }

  /** Recompute diff + verification, then redraw. Never throws into the render path. */
  private recompute(): void {
    this.recomputeCore();
    this.render();
  }

  /** The expensive half of {@link recompute}: diff and verify, no rendering. */
  private recomputeCore(): void {
    this.diff = undefined;
    this.facts = [];
    this.plan = undefined;
    this.planCheck = undefined;
    this.blockingError = undefined;
    this.mergeResult = undefined;
    this.mergeError = undefined;

    const a = this.a?.board;
    const b = this.b?.board;
    if (a && b) {
      try {
        this.diff = diffBoards(a, b, { showMetadataChanges: this.showMetadata });
        // The default view is the onion skin, where two near-identical boards
        // overlap almost everywhere. Dimming what did not change is what makes
        // the changes visible, so it starts on the moment there is a diff to
        // look at — until the user says otherwise.
        if (!this.emphasiseSettled) {
          this.emphasiseChanges = true;
          this.emphasiseSettled = true;
        }
        this.facts = compareBoards(a, b, this.diff);
        this.rebuildPlan();
      } catch (err) {
        if (err instanceof IncomparableFilesError) {
          this.blockingError = err.message;
          this.diff = undefined;
        } else {
          this.blockingError = `Internal error while comparing: ${(err as Error).message}`;
        }
      }
    }
  }

  // --- render ------------------------------------------------------------------

  private render(): void {
    // The logo stands in for the board until there is one to draw.
    const empty = this.a?.board === undefined && this.b?.board === undefined;
    this.els.watermark?.classList.toggle('on', empty);
    this.renderMergedNotice();
    this.updateTitle();

    this.renderLeft();
    this.renderMain();
    this.renderRight();
    this.syncView();
    this.revealSelectedChange();
  }

  /**
   * Bring the selected change into view in the list on the left.
   *
   * Selecting on the canvas or stepping with the arrow keys highlights a row
   * that may be hundreds of entries down a scrolled panel; without this the
   * highlight is invisible and the two halves of the UI disagree about what is
   * selected.
   *
   * Only fires when the selection actually changed. Re-scrolling on every render
   * would yank the panel out from under anyone editing a region or ticking a
   * checkbox.
   */
  private revealSelectedChange(): void {
    if (this.selectedKey === this.scrolledToKey) return;
    this.scrolledToKey = this.selectedKey;

    const row = this.els.selectedRow;
    if (!row) return;
    // `nearest` scrolls only as far as it must, so a row already on screen —
    // the usual case when the click came from the list itself — does not move.
    row.scrollIntoView?.({ block: 'nearest' });
  }

  private buildMain(): void {
    const canvas = h('canvas');
    this.els.canvas = canvas;
    const toolbar = h('div', { class: 'map-toolbar' });
    const layerBar = h('div', { class: 'layer-bar' });
    const watermark = h('img', {
      src: LOGO_FULL,
      alt: '',
      class: 'watermark',
    });
    this.els.watermark = watermark;
    const mergedNotice = h('div', { class: 'merged-notice' });
    this.els.mergedNotice = mergedNotice;
    const wrap = h('div', { class: 'map-wrap' }, canvas, watermark, mergedNotice);
    const footer = h('div', { class: 'view-footer' });

    this.els.toolbar = toolbar;
    this.els.layerBar = layerBar;
    this.els.footer = footer;

    clear(this.els.main).append(toolbar, layerBar, wrap, footer);

    try {
      const view = new BoardView(canvas);
      view.onSelect = (key) => {
        this.selectedKey = key;
        this.render();
      };
      view.onStats = (stats) => {
        this.viewStats = stats;
        this.renderFooter();
      };
      // Remembered, but deliberately without a re-render: rebuilding the DOM on
      // every pointer move would fight the drag.
      view.onSplitChange = (fraction) => {
        this.splitFraction = fraction;
      };
      view.onRectDrawn = (rect) => this.addSelection(rect);
      this.view = view;
      wrap.append(
        h(
          'div',
          { class: 'zoom-controls' },
          h('button', { class: 'zoom-in', title: 'Zoom in', 'aria-label': 'Zoom in', onclick: () => view.zoomBy(ZOOM_STEP) }, '+'),
          h('button', { class: 'zoom-out', title: 'Zoom out', 'aria-label': 'Zoom out', onclick: () => view.zoomBy(1 / ZOOM_STEP) }, '−'),
        ),
      );
    } catch (err) {
      // §7: the renderer is the least reliable component; losing it must not
      // take the diff, verification or merge down with it. Only the canvas is
      // removed — the rest of the area keeps its identity rather than becoming
      // a bare error message.
      canvas.remove();
      wrap.append(
        h(
          'div',
          { class: 'banner warn view-error' },
          `View unavailable: ${(err as Error).message}. Diff, verification and merge still work.`,
        ),
      );
    }
  }

  /**
   * A titled, collapsible panel.
   *
   * `id` keys the fold state, which lives on the app rather than in the DOM
   * because every render rebuilds the columns from scratch.
   */
  private panel(id: string, title: string, count: string | undefined, ...body: HTMLElement[]): HTMLElement {
    const wrap = h('div', { class: 'panel', 'data-panel': id });

    // Headers are pinned at all times — the ones you have scrolled past stack at
    // the top, the ones you have not reached stack at the bottom — so a header is
    // a "jump here" control rather than a fold. There is nothing to fold: a
    // section is as long as its content, and you scroll through it.
    const head = h(
      'h2',
      {
        title: `Scroll to ${title.toLowerCase()}`,
        onclick: () => this.scrollToSection(wrap),
      },
      h('span', { class: 'panel-title' }, title),
      count !== undefined ? h('span', { class: 'count' }, count) : null,
    );
    wrap.append(head);

    const content = h('div', { class: 'panel-content' });
    wrap.append(content);
    content.append(...body);
    return wrap;
  }

  /** The scrolling body of a panel, for callers that fill it in stages. */
  private static contentOf(panel: HTMLElement): HTMLElement {
    return panel.querySelector(':scope > .panel-content') as HTMLElement;
  }

  /**
   * Name the tab after whatever is loaded.
   *
   * With several comparisons open at once the tab strip is the only thing
   * distinguishing them, so it carries Board A's file name rather than the
   * same product name repeated.
   */
  /**
   * The standing reminder that Board A is not the file that was opened.
   *
   * Fixed over the canvas rather than in a column, because it is a statement
   * about what is on the canvas — and because it must be impossible to scroll
   * away from something this easy to forget.
   */
  private renderMergedNotice(): void {
    const el = this.els.mergedNotice;
    if (!el) return;
    clear(el);
    el.classList.toggle('on', this.boardAIsMerged);
    if (!this.boardAIsMerged) return;

    const saved = this.saveNotice !== undefined;
    const save = this.saveMergedButton('sm');
    el.append(
      h(
        'div',
        { class: 'merged-text' },
        h('strong', {}, 'Board A is the merged result'),
        h(
          'span',
          {},
          saved
            ? `Saved as ${this.mergeResult?.fileName ?? 'the merged file'}.`
            : `Not written to disk. The original ${this.originalA?.name ?? 'file'} is untouched.`,
        ),
      ),
      ...(save ? [save] : []),
      h(
        'button',
        {
          class: 'sm',
          title: `Discard the merged board and put ${this.originalA?.name ?? 'the original'} back`,
          onclick: () => this.revertMerge(),
        },
        'Revert',
      ),
    );
  }

  private updateTitle(): void {
    const loaded = this.a?.name;
    const title = loaded === undefined ? `${BRAND.short} - ${BRAND.titleTagline}` : `${BRAND.short} - ${loaded}`;
    if (typeof document !== 'undefined' && document.title !== title) document.title = title;
  }

  /** Toolbar, layer chips and footer. Rebuilt on each render; the canvas is not. */
  private renderMain(): void {
    const toolbar = this.els.toolbar;
    if (!toolbar) return;
    clear(toolbar);

    if (this.merging) {
      toolbar.append(
        h(
          'div',
          { class: 'merge-mode' },
          h('span', { class: 'badge' }, 'Choosing what to merge'),
          h('span', { class: 'note' }, 'Drag on the board · shift-drag to pan'),
          h(
            'button',
            {
              class: 'sm exit-merge',
              title: 'Leave merge mode and go back to reading the diff (Esc)',
              onclick: () => this.stopMerging(),
            },
            '× Exit merge mode',
          ),
        ),
      );
    }

    const modes: Array<[ViewMode, string, string]> = [
      ['a', 'A only', 'Show Board A on its own'],
      ['b', 'B only', 'Show Board B on its own'],
      ['onion', 'Onion skin', 'Both boards in the same pixels, faded between'],
      ['split', 'Split', 'Board A on the left, Board B on the right — drag the divider across'],
    ];
    toolbar.append(
      h(
        'div',
        { class: 'seg' },
        ...modes.map(([m, label, hint]) =>
          h(
            'button',
            {
              class: this.mode === m ? 'sm on' : 'sm',
              title: hint,
              onclick: () => {
                this.mode = m;
                this.render();
              },
            },
            label,
          ),
        ),
      ),
    );

    if (this.mode === 'onion') {
      const slider = h('input', {
        type: 'range',
        min: '0',
        max: '100',
        step: '1',
        value: String(Math.round(this.blend * 100)),
        class: 'blend',
        title: 'Fade between Board A and Board B',
        oninput: (ev: Event) => {
          this.blend = Number((ev.target as HTMLInputElement).value) / 100;
          this.syncView();
          this.renderBlendLabel();
        },
      });
      this.els.blendSlider = slider;
      const label = h('span', { class: 'note blend-label' });
      this.els.blendLabel = label;
      toolbar.append(
        h('span', { class: 'note swatch-a' }, 'A'),
        slider,
        h('span', { class: 'note swatch-b' }, 'B'),
        label,
      );
    }

    if (this.mode === 'split') {
      toolbar.append(
        h(
          'button',
          {
            class: 'sm',
            title: 'Put the divider back in the middle',
            onclick: () => {
              this.splitFraction = 0.5;
              this.render();
            },
          },
          'Centre divider',
        ),
      );
    }

    toolbar.append(
      h(
        'button',
        { class: 'sm with-icon', title: 'Zoom to fit the whole board', onclick: () => this.view?.fit() },
        svgIcon('M2 6V2h4 M10 2h4v4 M14 10v4h-4 M6 14H2v-4', 12),
        'Fit',
      ),
      toggle('Tint A/B', this.tintBoards, (on) => {
        this.tintBoards = on;
        this.render();
      }),
      toggle('Dim unchanged', this.emphasiseChanges, (on) => {
        this.emphasiseChanges = on;
        this.emphasiseSettled = true;
        this.render();
      }),
      toggle('Diff boxes', this.showOverlay, (on) => {
        this.showOverlay = on;
        this.render();
      }),
      h(
        'span',
        { class: 'note keys' },
        h('kbd', {}, '\u2190'),
        h('kbd', {}, '\u2192'),
        ' step  ',
        h('kbd', {}, 'f'),
        ' fit  ',
        h('kbd', {}, '1'),
        h('kbd', {}, '2'),
        h('kbd', {}, '3'),
        h('kbd', {}, '4'),
        ' view',
      ),
    );

    this.renderBlendLabel();
    this.renderLayerBar();
    this.renderFooter();
  }

  private renderBlendLabel(): void {
    const label = this.els.blendLabel;
    if (!label) return;
    const pct = Math.round(this.blend * 100);
    label.textContent = pct === 0 ? 'A only' : pct === 100 ? 'B only' : `A ${100 - pct}% / B ${pct}%`;
  }

  private renderLayerBar(): void {
    const bar = this.els.layerBar;
    if (!bar) return;
    clear(bar);
    const available = this.view?.availableLayers() ?? [];
    if (available.length === 0) return;

    bar.append(
      h(
        'button',
        {
          class: 'sm',
          title: 'Show every layer this board uses',
          onclick: () => {
            this.visibleLayers = new Set(available);
            this.render();
          },
        },
        'All',
      ),
      h(
        'button',
        {
          class: 'sm',
          title: 'Back to copper, silkscreen and the board outline',
          onclick: () => {
            this.visibleLayers = this.view?.suggestedLayers() ?? new Set();
            this.render();
          },
        },
        'Reset',
      ),
      h('span', { class: 'note keys layer-hint' }, h('kbd', {}, 'shift'), ' solo  ', h('kbd', {}, 'ctrl'), ' all'),
    );

    for (const layer of available) {
      const on = this.visibleLayers.has(layer);
      const chip = h(
        'button',
        {
          class: on ? 'chip on' : 'chip',
          title: `${layerLabel(layer)}\nClick to toggle · Shift+click to show only this · Ctrl+click to show all`,
          onclick: (ev: MouseEvent) => {
            // Isolating one layer and getting everything back are the two moves
            // worth a shortcut: hunting for a trace on an inner layer means
            // hiding twenty chips one at a time otherwise.
            if (ev.shiftKey) this.visibleLayers = new Set([layer]);
            else if (ev.ctrlKey || ev.metaKey) this.visibleLayers = new Set(available);
            else if (this.visibleLayers.has(layer)) this.visibleLayers.delete(layer);
            else this.visibleLayers.add(layer);
            this.render();
          },
        },
        layerLabel(layer),
      );
      const dot = h('i', { class: 'dot' });
      dot.style.background = layerStyle(layer).color;
      chip.prepend(dot);
      bar.append(chip);
    }

  }

  private renderFooter(): void {
    const footer = this.els.footer;
    if (!footer) return;
    clear(footer);

    footer.append(
      h(
        'span',
        { class: 'legend' },
        legendItem('#3fb950', 'added'),
        legendItem('#f85149', 'removed'),
        legendItem('#e3b341', 'modified / moved'),
        legendItem('#d24bd2', 'not rendered'),
        legendItem('#4a9eff', 'region'),
      ),
    );

    if ((this.mode === 'onion' || this.mode === 'split') && this.tintBoards) {
      footer.append(
        h('span', { class: 'legend' }, legendItem(TINT_A, 'Board A'), legendItem(TINT_B, 'Board B')),
      );
    }

    footer.append(
      h(
        'span',
        { class: 'note credit' },
        'created by ',
        h(
          'a',
          { href: BRAND.companyUrl, target: '_blank', rel: 'noopener noreferrer' },
          BRAND.company,
        ),
      ),
    );

    const stats = this.viewStats;
    if (stats) {
      const unrendered = stats.placeholdersA + stats.placeholdersB;
      footer.append(
        h(
          'span',
          { class: 'note' },
          `${stats.shapes.toLocaleString()} shapes drawn, ${stats.culled.toLocaleString()} culled \u00b7 ${stats.ms.toFixed(0)} ms`,
        ),
      );
      if (unrendered > 0) {
        footer.append(
          h(
            'span',
            { class: 'note warn' },
            `${unrendered} object(s) could not be drawn and are marked in magenta.`,
          ),
        );
      }
    }
  }

  private syncView(): void {
    const view = this.view;
    if (!view) return;

    const input = (visibleLayers: Set<string>): ViewInput => ({
      ...(this.a?.board ? { a: this.a.board } : {}),
      ...(this.b?.board ? { b: this.b.board } : {}),
      ...(this.diff ? { diff: this.diff } : {}),
      selections: this.selections,
      ...(this.activeSelectionId ? { activeSelectionId: this.activeSelectionId } : {}),
      merging: this.merging,
      ...(this.merging && this.plan ? { takenKeys: this.plan.plan.take } : {}),
      mode: this.mode,
      ...(this.selectedKey ? { selectedKey: this.selectedKey } : {}),
      visibleLayers,
      blend: this.blend,
      split: this.splitFraction,
      tintBoards: this.tintBoards,
      emphasiseChanges: this.emphasiseChanges,
      showOverlay: this.showOverlay,
    });

    try {
      // The view has to see the boards before it can say which layers they use,
      // so the boards go in first. Seeding visibility beforehand asked an empty
      // view what it contained, got nothing, and drew a board with every layer
      // switched off — regions but no copper, until some later render happened
      // to run the seeding again.
      view.setInput(input(this.visibleLayers));

      if (!this.layersInitialised) {
        const suggested = view.suggestedLayers();
        if (suggested.size > 0) {
          this.visibleLayers = suggested;
          this.layersInitialised = true;
          this.renderLayerBar();
          // Refit: the first fit had nothing visible to frame and fell back to
          // the whole file, including any documentation graphics far outside
          // the board.
          view.setInput(input(this.visibleLayers), true);
        }
      }
    } catch {
      /* the view is optional; never let it break the panels */
    }
  }

  // --- about ------------------------------------------------------------------------

  /** A modal with credits, the disclaimer, and where to find more. */
  private showAbout(): void {
    this.closeDialog();

    const close = (): void => this.closeDialog();
    const panel = h(
      'div',
      { class: 'dialog', role: 'dialog', 'aria-label': `About ${BRAND.name}` },
      h(
        'div',
        { class: 'dialog-head' },
        h('img', { src: LOGO_MARK, alt: '', class: 'dialog-logo' }),
        h(
          'div',
          {},
          h('h2', {}, BRAND.name),
          h('p', { class: 'tagline' }, BRAND.tagline),
        ),
        h('button', { class: 'sm dialog-close', title: 'Close', onclick: close }, '×'),
      ),
      h(
        'div',
        { class: 'dialog-body' },
        h(
          'p',
          {},
          'Compare two revisions of a KiCad board, see exactly what changed, and build a merged file by ' +
            'choosing which changes to keep — one at a time, or a whole area at once. Every merge is ' +
            'checked before it is offered to you, and refused rather than repaired if anything looks wrong.',
        ),
        h(
          'p',
          {},
          'Created by ',
          h('strong', {}, BRAND.author),
          ' ',
          h('a', { href: `mailto:${BRAND.authorEmail}` }, `<${BRAND.authorEmail}>`),
          '.',
        ),
        h(
          'div',
          { class: 'row' },
          h('a', { class: 'dialog-link', href: BRAND.docsUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Documentation ↗'),
          h('a', { class: 'dialog-link', href: BRAND.repoUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Source on gitlab ↗'),
        ),
        h(
          'p',
          { class: 'disclaimer' },
          `${BRAND.name} is provided as it stands, without warranty of any kind. `,
          h('strong', {}, BRAND.company),
          ' accepts no liability for any loss or damage arising from its use — including, and ' +
            'especially, to your board files. Keep your work in version control, and check the ' +
            'merged output in KiCad before you send anything to a fabricator.',
        ),
        h(
          'p',
          { class: 'note' },
          'Everything runs in this tab. Board files are never uploaded; the only network requests ' +
            'are to a repository host you name.',
        ),
      ),
    );

    const backdrop = h('div', { class: 'dialog-backdrop', onclick: (ev: MouseEvent) => {
      if (ev.target === backdrop) close();
    } }, panel);

    this.els.dialog = backdrop;
    this.root.append(backdrop);
    (panel.querySelector('.dialog-close') as HTMLElement | null)?.focus();
  }

  private closeDialog(): void {
    this.els.dialog?.remove();
    delete this.els.dialog;
  }

  // --- left column ---------------------------------------------------------------

  /**
   * Bring a section to rest just below the headers that pin above it.
   *
   * Not `scrollIntoView`: the section wrapper generates no box, and the target
   * is not the top of the column but the underside of the pinned stack.
   */
  private scrollToSection(wrap: HTMLElement): void {
    const col = wrap.closest('.col') as HTMLElement | null;
    const content = wrap.querySelector(':scope > .panel-content') as HTMLElement | null;
    const head = wrap.querySelector(':scope > h2') as HTMLElement | null;
    if (!col || !content || !head) return;

    col.scrollTop = sectionScrollTarget({
      scrollTop: col.scrollTop,
      columnTop: col.getBoundingClientRect().top,
      contentTop: content.getBoundingClientRect().top,
      headersAbove: Number(wrap.style.getPropertyValue('--panel-above') || '0'),
      headerHeight: head.getBoundingClientRect().height || PANEL_HEADER_PX,
    });
  }

  /**
   * Give each header its slot in the sticky stack.
   *
   * Headers pin at `top: i × H` and `bottom: (n − 1 − i) × H`, so the ones
   * scrolled past stack upward and the ones not yet reached stack downward —
   * every heading is on screen whatever you are reading, and the content
   * between the two stacks is the section you are in.
   *
   * The `.panel` wrapper generates no box (`display: contents`), so a header's
   * sticky containing block is the scrolling column rather than its own
   * section. Without that a header vanishes the moment its own section scrolls
   * past, which is exactly what went wrong the first time.
   */
  private stackHeaders(col: HTMLElement): void {
    // Anything pinned above the sections — the brand header, the merge banner —
    // takes room off the top of the stack, and stacks among itself.
    let above = 0;
    for (const pinned of [...col.querySelectorAll(':scope > .pinned-top')] as HTMLElement[]) {
      pinned.style.setProperty('top', `${above}px`);
      above += pinned.getBoundingClientRect().height;
    }
    col.style.setProperty('--stack-top-offset', `${above}px`);

    const panels = ([...col.querySelectorAll(':scope > .panel')] as HTMLElement[]).filter(
      (panel) => panel.querySelector(':scope > h2') !== null,
    );
    panels.forEach((panel, i) => {
      panel.style.setProperty('--panel-above', String(i));
      panel.style.setProperty('--panel-below', String(panels.length - 1 - i));
    });
  }

  private renderLeft(): void {
    delete this.els.selectedRow;
    const col = clear(this.els.left);
    this.root.classList.toggle('left-collapsed', this.leftCollapsed);

    if (this.leftCollapsed) {
      const n = this.diff?.substantive.length;
      const differing = this.facts.filter((f) => !f.same).length;
      const indicators: HTMLElement[] = [];
      if (this.blockingError) {
        indicators.push(h('span', { class: 'rail-badge bad', title: this.blockingError }, '!'));
      } else if (n !== undefined) {
        indicators.push(
          h(
            'span',
            {
              class: n + differing === 0 ? 'rail-badge same' : 'rail-badge differs',
              title:
                `${plural(n, 'difference')}` + (differing > 0 ? `, ${plural(differing, 'board-level difference')}` : ''),
            },
            String(n),
          ),
        );
      }
      if (this.merging) indicators.push(h('span', { class: 'rail-badge merging', title: 'Merge mode' }, 'M'));
      col.append(this.rail('left', indicators));
      return;
    }

    col.append(
      h(
        'div',
        { class: 'title-bar pinned-top' },
        h('img', { src: LOGO_MARK, alt: '', class: 'brand-mark' }),
        h(
          'div',
          { class: 'brand-text' },
          h('h1', {}, BRAND.short),
          h('p', { class: 'tagline' }, BRAND.tagline),
        ),
        h(
          'button',
          { class: 'sm about-button', title: `About ${BRAND.name}`, onclick: () => this.showAbout() },
          'About',
        ),
        this.collapseButton('left'),
      ),
    );

    col.append(this.sourcePanel());
    if (this.blockingError) col.append(h('div', { class: 'banner bad' }, this.blockingError));
    // Verdict first and never collapsible: §6.8 makes it the most important
    // element on the page. The detail behind it folds away separately.
    col.append(this.verdictPanel());
    col.append(this.factsPanel());
    col.append(this.changesPanel());
    col.append(this.coveragePanel());
    this.stackHeaders(col);
  }

  /** The button that folds a sidebar to its rail, or opens it again. */
  private collapseButton(side: 'left' | 'right'): HTMLElement {
    const collapsed = side === 'left' ? this.leftCollapsed : this.rightCollapsed;
    const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
    return h(
      'button',
      {
        class: 'sm icon collapse-toggle',
        title: label,
        'aria-label': label,
        'aria-expanded': String(!collapsed),
        onclick: () => {
          if (side === 'left') this.leftCollapsed = !this.leftCollapsed;
          else this.rightCollapsed = !this.rightCollapsed;
          this.render();
        },
      },
      svgIcon(side === 'left' ? 'M2 3h12v10H2z M6 3v10' : 'M2 3h12v10H2z M10 3v10', 14),
    );
  }

  /** A folded sidebar: the logo, the way back out, and a few status badges. */
  private rail(side: 'left' | 'right', indicators: HTMLElement[]): HTMLElement {
    return h(
      'div',
      { class: `rail rail-${side}` },
      h('img', { src: LOGO_MARK, alt: BRAND.short, class: 'rail-logo' }),
      this.collapseButton(side),
      h('div', { class: 'rail-indicators' }, ...indicators),
    );
  }

  /**
   * Where the two boards come from.
   *
   * A segmented control rather than tabs: the app already uses one for the view
   * mode, the panel is narrow, and it states plainly that the two options are
   * alternatives rather than sections to browse.
   */
  private sourcePanel(): HTMLElement {
    const body = h('div', { class: 'panel-body' });

    body.append(
      h(
        'div',
        { class: 'seg full' },
        ...(['local', 'git'] as const).map((mode) =>
          h(
            'button',
            {
              class: this.sourceMode === mode ? 'sm on' : 'sm',
              onclick: () => {
                this.sourceMode = mode;
                this.render();
              },
            },
            mode === 'local' ? 'Local files' : 'Git repository',
          ),
        ),
      ),
    );

    if (this.sourceMode === 'local') this.appendLocalSource(body);
    else this.appendGitSource(body);
    this.appendMergedFileActions(body);

    return this.panel('source', 'Source', undefined, body);
  }

  /**
   * Where a merged Board A can be written out.
   *
   * In the Source panel because that is where Board A's identity lives, and
   * because merging is a mode the user leaves — the merge panel goes with it,
   * and the file must not go with the panel.
   */
  private appendMergedFileActions(body: HTMLElement): void {
    const save = this.saveMergedButton();
    if (!save) return;
    body.append(
      h(
        'div',
        { class: 'merged-source' },
        h('div', { class: 'note warn' }, 'Board A is the merged result and is not on disk.'),
        h(
          'div',
          { class: 'row' },
          save,
          h('button', { class: 'sm', onclick: () => this.revertMerge() }, 'Revert'),
        ),
        this.saveNotice ? h('div', { class: 'note' }, this.saveNotice) : null,
      ),
    );
  }

  private appendLocalSource(body: HTMLElement): void {
    const slot = (which: 'a' | 'b', label: string): HTMLElement => {
      const loaded = this[which];
      const zone = h('div', { class: loaded ? 'dropzone loaded' : 'dropzone' });
      zone.append(h('div', { class: 'slot' }, label));
      if (!loaded) {
        zone.append(h('div', { class: 'note' }, 'Drop a .kicad_pcb here, or click to choose'));
      } else if (loaded.error) {
        zone.append(h('div', { class: 'name' }, loaded.name), h('div', { class: 'note bad' }, loaded.error));
      } else {
        const board = loaded.board!;
        const stats = board.stats();
        zone.append(
          h('div', { class: 'name' }, loaded.name),
          h(
            'div',
            { class: 'meta' },
            `KiCad ${board.generatorVersion ?? '?'} · format ${board.version ?? '?'} · ` +
              `${plural(stats.objects, 'object')} · ${plural(board.allNetNames().size, 'net')}`,
          ),
        );
      }
      makeDropTarget(zone, '.kicad_pcb', (file) => void this.load(which, file));
      return zone;
    };

    body.append(slot('a', 'Board A'), slot('b', 'Board B'));
  }

  private appendGitSource(body: HTMLElement): void {
    const input = h('input', {
      type: 'text',
      value: this.repoUrl,
      placeholder: 'https://github.com/owner/project',
      oninput: (ev: Event) => {
        this.repoUrl = (ev.target as HTMLInputElement).value;
      },
      onkeydown: (ev: KeyboardEvent) => {
        if (ev.key === 'Enter') void this.openRepo();
      },
    });

    body.append(
      h('label', {}, 'Public repository URL', input),
      h(
        'div',
        { class: 'row' },
        h(
          'button',
          { class: 'sm primary', disabled: this.repoBusy !== undefined, onclick: () => void this.openRepo() },
          this.repo ? 'Reload' : 'Open',
        ),
        this.repoBusy ? h('span', { class: 'note' }, this.repoBusy) : null,
      ),
    );

    if (this.repoError) {
      body.append(
        h(
          'div',
          { class: 'banner bad repo-error' },
          h('div', {}, this.repoError.message),
          h('div', { class: 'note' }, this.repoError.hint),
        ),
      );
    }

    const repo = this.repo;
    if (!repo) {
      body.append(
        h(
          'div',
          { class: 'note' },
          'GitHub, GitLab and Codeberg, including self-hosted GitLab and Forgejo. Public repositories ' +
            'only — nothing is uploaded and no credentials are ever sent.',
        ),
      );
      return;
    }

    body.append(
      h(
        'div',
        { class: 'note' },
        `${repo.info.fullName} · default branch ${repo.info.defaultBranch} · ` +
          `${plural(repo.files.length, 'board file')}`,
      ),
    );

    // One path, two revisions of it — comparing a file against itself is the
    // whole point, so the path is chosen once rather than per slot.
    body.append(
      h(
        'label',
        {},
        'Board file',
        h(
          'select',
          {
            onchange: (ev: Event) => void this.selectRepoPath((ev.target as HTMLSelectElement).value),
          },
          ...repo.files.map((f) => h('option', { value: f, ...(f === this.repoPath ? { selected: 'selected' } : {}) }, f)),
        ),
      ),
    );

    for (const slot of ['a', 'b'] as const) {
      body.append(this.refPicker(slot));
    }

    if (this.repoPath) {
      const link = h(
        'a',
        {
          href: repo.provider.webUrl(this.repoRef.a ?? repo.info.defaultBranch, this.repoPath),
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'note',
        },
        'View on host ↗',
      );
      body.append(link);
    }
  }

  private refPicker(slot: 'a' | 'b'): HTMLElement {
    const repo = this.repo!;
    const selected = this.repoRef[slot];
    const loaded = this[slot];

    const option = (r: GitRef): HTMLElement =>
      h(
        'option',
        { value: r.id, title: r.detail ?? r.label, ...(r.id === selected ? { selected: 'selected' } : {}) },
        r.label,
      );

    const branches = repo.refs.filter((r) => r.kind === 'branch');
    const tags = repo.refs.filter((r) => r.kind === 'tag');
    const groups: HTMLElement[] = [];
    if (selected === undefined) groups.push(h('option', { value: '' }, '— choose a revision —'));
    if (branches.length > 0) groups.push(h('optgroup', { label: 'Branches' }, ...branches.map(option)));
    if (tags.length > 0) groups.push(h('optgroup', { label: 'Tags' }, ...tags.map(option)));
    if (this.repoCommits.length > 0) {
      groups.push(
        h('optgroup', { label: `Commits touching this file` }, ...this.repoCommits.map(option)),
      );
    }

    const status = loaded?.error
      ? h('div', { class: 'note bad' }, loaded.error)
      : loaded?.board
        ? h(
            'div',
            { class: 'note' },
            `${plural(loaded.board.stats().objects, 'object')} · ${plural(loaded.board.allNetNames().size, 'net')}`,
          )
        : null;

    return h(
      'div',
      { class: 'ref-picker' },
      h(
        'label',
        {},
        slot === 'a' ? 'Board A revision' : 'Board B revision',
        h(
          'select',
          {
            disabled: this.repoPath === undefined,
            onchange: (ev: Event) => void this.selectRepoRef(slot, (ev.target as HTMLSelectElement).value),
          },
          ...groups,
        ),
      ),
      status,
    );
  }

  // --- repository actions -------------------------------------------------------

  private async openRepo(): Promise<void> {
    // A new repository is a new comparison: keeping the previous boards and
    // their regions on screen would invite reading one board's verdict as the
    // other's.
    this.resetBoards();
    this.repoError = undefined;
    this.repo = undefined;
    this.repoPath = undefined;
    this.repoCommits = [];
    this.repoRef = {};
    this.repoCache.clear();
    this.repoBusy = 'Contacting host…';
    this.render();

    try {
      const { provider, parsed } = await resolveRepo(this.repoUrl, this.fetchImpl);
      this.repoBusy = 'Reading repository…';
      this.render();

      const info = await provider.info();
      const ref = parsed.ref ?? info.defaultBranch;
      const [files, refs] = await Promise.all([provider.listBoardFiles(ref), provider.listRefs()]);

      this.repo = { provider, info, refs, files };
      // A link straight to a board selects it; otherwise take the only one, or
      // the first, and let the user change it.
      const fromUrl = parsed.path && files.includes(parsed.path) ? parsed.path : undefined;
      this.repoBusy = undefined;
      await this.selectRepoPath(fromUrl ?? files[0]!);
    } catch (err) {
      this.repoBusy = undefined;
      this.repoError =
        err instanceof RepoAccessError
          ? err
          : new RepoAccessError('http', `Could not open the repository: ${(err as Error).message}`, 'Check the URL and try again.');
      this.render();
    }
  }

  private async selectRepoPath(path: string): Promise<void> {
    const repo = this.repo;
    if (!repo) return;
    // A different board file is a different comparison.
    if (this.repoPath !== undefined && this.repoPath !== path) {
      this.resetBoards();
      this.repoRef = {};
    }
    this.repoPath = path;
    this.repoCommits = [];
    this.repoBusy = 'Reading history…';
    this.render();

    try {
      this.repoCommits = await repo.provider.listCommits(repo.info.defaultBranch, path);
    } catch {
      // History is a convenience: branches and tags still work without it.
      this.repoCommits = [];
    }
    this.repoBusy = undefined;
    this.render();
  }

  private async selectRepoRef(slot: 'a' | 'b', ref: string): Promise<void> {
    const repo = this.repo;
    const path = this.repoPath;
    if (!repo || !path || ref === '') return;

    this.repoRef[slot] = ref;
    this.repoError = undefined;
    this.repoBusy = `Fetching ${slot === 'a' ? 'Board A' : 'Board B'}…`;
    this.render();

    const key = `${repo.info.fullName}@${ref}:${path}`;
    try {
      let text = this.repoCache.get(key);
      if (text === undefined) {
        text = await repo.provider.fetchFile(ref, path);
        this.repoCache.set(key, text);
      }
      this.repoBusy = undefined;
      // Goes through loadBoard, so a new revision of Board A re-derives the
      // regions from *that* revision's outlines.
      await this.loadBoard(slot, repoFileName(path, ref), text);
    } catch (err) {
      this.repoBusy = undefined;
      this.repoError =
        err instanceof RepoAccessError
          ? err
          : new RepoAccessError('http', `Could not fetch the board: ${(err as Error).message}`, 'Try another revision.');
      this.render();
    }
  }

  /**
   * The headline.
   *
   * Not a verdict any more. PASS/FAIL answered a question only the region merge
   * asked — *is every difference inside the agreed area* — and it stopped meaning
   * anything once the user became the one deciding which differences to take. So
   * this says what is true: how many differences there are.
   */
  private verdictPanel(): HTMLElement {
    const wrap = h('div', { class: 'panel' });
    const idle = (word: string, sub: string) =>
      h('div', { class: 'verdict idle' }, h('div', { class: 'word' }, word), h('div', { class: 'sub' }, sub));

    if (!this.a?.board || !this.b?.board) {
      wrap.append(idle('Load two files', 'Comparing needs two boards.'));
      return wrap;
    }
    if (this.blockingError !== undefined) {
      wrap.append(idle('Cannot compare', this.blockingError));
      return wrap;
    }
    const diff = this.diff;
    if (!diff) {
      wrap.append(idle('Not compared', 'Comparison unavailable.'));
      return wrap;
    }

    const n = diff.substantive.length;
    const differing = this.facts.filter((f) => !f.same).length;
    const sub =
      n === 0
        ? differing === 0
          ? 'These two files are identical.'
          : `No object differs, but ${plural(differing, 'board-level difference')}.`
        : differing === 0
          ? 'Board-level state is identical.'
          : `Plus ${plural(differing, 'board-level difference')}.`;

    wrap.append(
      h(
        'div',
        { class: n === 0 && differing === 0 ? 'verdict same' : 'verdict differs' },
        h('div', { class: 'word' }, n === 0 ? 'No differences' : plural(n, 'difference')),
        h('div', { class: 'sub' }, sub),
      ),
    );

    // The one control that moves from reading to committing. Deliberately its
    // own gesture: nothing else on this screen can produce a file.
    if (!this.merging) {
      wrap.append(
        h(
          'div',
          { class: 'start-merge' },
          h(
            'button',
            {
              class: 'primary',
              disabled: n === 0 && differing === 0,
              onclick: () => this.startMerging(),
            },
            'Start merging…',
          ),
          h('div', { class: 'note' }, 'Choose what to take. Nothing is written until you download it.'),
        ),
      );
    }

    return wrap;
  }

  /**
   * How the two files differ at board level.
   *
   * These used to be checks with a pass or a fail. They are descriptions now:
   * whether any of it blocks a merge depends on what you select, and that
   * question is answered against the plan, on the right.
   */
  private factsPanel(): HTMLElement {
    if (this.facts.length === 0) return h('div');

    const list = h('ul', { class: 'checklist checks' });
    for (const f of this.facts) {
      list.append(
        h(
          'li',
          {
            class: f.same ? 'ok' : 'note',
            title: `${f.label}\n\n${f.help}\n\n${f.summary}`,
          },
          h('span', { class: 'id' }, f.same ? '=' : '≠'),
          h('span', {}, h('div', { class: 'what' }, f.label), h('div', { class: 'why' }, f.summary)),
        ),
      );
    }

    const body: HTMLElement[] = [list];
    const differing = this.facts.filter((f) => !f.same);

    if (differing.length > 0) {
      const details = h('div', { class: 'panel-body' });
      for (const fact of differing) {
        for (const detail of fact.details.slice(0, 20)) {
          const el = h(
            'div',
            {
              class: 'violation informational',
              title: fact.help,
              onclick: () => {
                if (detail.bbox) this.view?.zoomTo(detail.bbox);
                this.render();
              },
            },
            h('div', { class: 'vt' }, h('span', { class: 'tag' }, fact.label), detail.title),
            h('div', { class: 'vd' }, detail.detail),
          );
          if (detail.fields && detail.fields.length > 0) {
            const fields = h('div', { class: 'fields' });
            for (const f of detail.fields.slice(0, 12)) fields.append(h('div', {}, formatFieldChange(f)));
            if (detail.fields.length > 12) fields.append(h('div', {}, `… and ${detail.fields.length - 12} more`));
            el.append(fields);
          }
          details.append(el);
        }
        if (fact.details.length > 20) {
          details.append(h('div', { class: 'note' }, `… and ${fact.details.length - 20} more`));
        }
      }
      body.push(details);
    }

    return this.panel(
      'facts',
      'Board state',
      differing.length > 0 ? `${differing.length} differ` : 'all identical',
      ...body,
    );
  }

  private changesPanel(): HTMLElement {
    const body = h('div', { class: 'panel-body' });
    const diff = this.diff;

    body.append(
      h(
        'label',
        { class: 'checkbox' },
        h('input', {
          type: 'checkbox',
          checked: this.showMetadata,
          onchange: (ev: Event) => {
            this.showMetadata = (ev.target as HTMLInputElement).checked;
            this.recompute();
          },
        }),
        'Show metadata changes (UUID-only, zone fill cache)',
      ),
    );

    // While merging, the useful question about a change is whether any selection
    // claims it. Asking it once as a filter beats labelling every row.
    if (this.merging && this.plan) {
      body.append(
        h(
          'label',
          { class: 'checkbox' },
          h('input', {
            type: 'checkbox',
            checked: this.onlySelected,
            onchange: (ev: Event) => {
              this.onlySelected = (ev.target as HTMLInputElement).checked;
              this.render();
            },
          }),
          'Only changes a selection claims',
        ),
      );
    }

    if (!diff) {
      body.append(h('div', { class: 'empty' }, 'Load both files to see changes.'));
      return this.panel('changes', 'Changes', undefined, body);
    }

    body.append(
      h(
        'div',
        { class: 'note' },
        `+${diff.counts.added} added · −${diff.counts.removed} removed · ` +
          `~${diff.counts.modified} modified · →${diff.counts.moved} moved · ${diff.counts.unchanged} unchanged`,
      ),
    );
    if (diff.geometricMatches > 0) {
      body.append(
        h(
          'div',
          { class: 'note warn' },
          `${plural(diff.geometricMatches, 'object')} matched by geometry rather than UUID — ` +
            'consistent with this region having been copy/pasted.',
        ),
      );
    }
    if (!this.showMetadata && (diff.uuidOnlyChanges > 0 || diff.fillCacheOnlyChanges > 0)) {
      body.append(
        h(
          'div',
          { class: 'note' },
          `Suppressed: ${diff.uuidOnlyChanges} UUID-only, ${diff.fillCacheOnlyChanges} zone fill cache.`,
        ),
      );
    }

    // Global board state is always shown, even when empty (spec §6.3).
    const global = h('div', { class: 'note' });
    if (diff.global.identical) {
      global.textContent = 'Global board state: identical.';
    } else {
      global.className = 'note bad';
      const changed = diff.global.blocks.filter((x) => x.status !== 'identical' && x.status !== 'absent');
      global.textContent =
        'Global board state DIFFERS: ' +
        (changed.map((c) => c.block).join(', ') || 'net table') +
        '. A region merge keeps Board A\'s version and discards Board B\'s.';
    }
    body.append(global);

    const total = diff.substantive.length;
    const taken = this.merging ? this.plan?.plan.take : undefined;
    const claims = this.merging ? claimsByChange(diff, this.selections) : undefined;
    const selectedCount = taken ? diff.substantive.filter((c) => taken.has(c.key)).length : total;
    const count = taken && selectedCount !== total ? `${selectedCount} of ${total} selected` : String(total);
    const wrap = this.panel('changes', 'Changes', count, body);
    if (total === 0) {
      body.append(h('div', { class: 'empty' }, 'No object differences.'));
      return wrap;
    }
    if (this.onlySelected && selectedCount === 0) {
      body.append(h('div', { class: 'empty' }, `None of the ${total} change(s) are in a selection yet.`));
      return wrap;
    }

    const list = h('ul', { class: 'changes' });
    let lastType: string | undefined;
    for (const change of diff.substantive) {
      const obj = change.b ?? change.a;
      const type = obj?.type ?? '?';
      const heading = type !== lastType ? h('li', { class: 'group-head' }, type) : null;
      const isTaken = taken?.has(change.key);
      if (this.onlySelected && isTaken === false) continue;
      const selected = change.key === this.selectedKey;
      const classes = ['changes-item', change.kind];
      if (selected) classes.push('selected');
      if (isTaken === false) classes.push('unselected');

      const row = h(
        'li',
        {
          class: classes.join(' '),
          title: this.changeTooltip(change),
          onclick: () => this.select(change),
        },
        h('span', { class: 'glyph' }, glyphFor(change.kind)),
        h(
          'span',
          { class: 'txt' },
          h('span', { class: 'what' }, obj ? Board.describe(obj) : change.key),
          // What actually changed, on the row itself. Leaving it in the tooltip
          // meant a net reassignment — the whole point of the comparison —
          // was invisible until the pointer happened to rest on the right line.
          summariseChange(change),
        ),
      );
      const claiming = claims?.get(change.key);
      if (claiming !== undefined && claiming.length > 0) {
        const tags = h('span', { class: 'claims' });
        for (const sel of claiming) {
          const dot = h('i', { class: 'swatch', title: sel.name });
          dot.style.background = selectionColor(this.selections.indexOf(sel));
          tags.append(dot);
        }
        row.prepend(tags);
      }
      if (selected) this.els.selectedRow = row;
      // Added only now, so a heading whose rows were all filtered out never
      // appears on its own.
      if (heading) {
        list.append(heading);
        lastType = type;
      }
      list.append(row);
    }
    App.contentOf(wrap).append(list);
    return wrap;
  }

  private changeTooltip(change: ObjectChange): string {
    const lines = [describeChange(change, Board.describe)];
    if (change.displacement) {
      lines.push(`moved by (${mm(change.displacement[0])}, ${mm(change.displacement[1])}) mm`);
    }
    if (change.rotation) lines.push(`rotation ${change.rotation[0]}° → ${change.rotation[1]}°`);
    for (const f of change.fields.slice(0, 10)) lines.push(formatFieldChange(f));
    if (change.fields.length > 10) lines.push(`… and ${change.fields.length - 10} more`);
    if (this.merging && this.diff) {
      const claiming = claimsByChange(this.diff, this.selections).get(change.key);
      lines.push(
        claiming === undefined
          ? 'in no selection — Board A keeps its own version'
          : `taken by ${claiming.map((sel) => `"${sel.name}"`).join(', ')}`,
      );
    }
    lines.push(`matched by ${change.matchedBy}`);
    lines.push(formatBBox(change.bbox));
    return lines.join('\n');
  }

  private select(change: ObjectChange): void {
    this.selectedKey = change.key;
    this.view?.zoomTo(change.bbox);
    this.render();
  }

  /**
   * Coverage panel (a headless subset of §6.7).
   *
   * Without a renderer there is nothing to reconcile counts against, so what is
   * reported here is what *our* parser could not fully account for: tokens the
   * model does not recognise, boxes that are approximations, and parser
   * warnings. The escalation rule still applies — anything unrecognised inside
   * the active region is called out loudly.
   */
  private coveragePanel(): HTMLElement {
    const body = h('div', { class: 'panel-body' });
    const boards = [this.a, this.b].filter((f): f is LoadedFile => f?.board !== undefined);
    if (boards.length === 0) {
      body.append(h('div', { class: 'empty' }, 'Nothing loaded.'));
      return this.panel('coverage', 'Coverage', undefined, body);
    }

    let flagged = 0;
    for (const file of boards) {
      const board = file.board!;
      const stats = board.stats();
      const unknown = board.objects.filter((o) => !o.known);
      const approximate = board.objects.filter((o) => o.bboxApproximate);
      flagged += unknown.length;

      body.append(
        h(
          'div',
          { class: 'note' },
          `${file.name}: ${stats.objects - stats.unknownObjects} of ${stats.objects} objects fully modelled, ` +
            `${stats.unknownObjects} unrecognised, ${approximate.length} with an approximate box.`,
        ),
      );

      if (unknown.length > 0) {
        body.append(
          h(
            'div',
            { class: 'note warn' },
            `Unrecognised tokens outside the region: ${[...new Set(unknown.map((o) => o.type))].join(', ')}.`,
          ),
        );
      }

      for (const w of board.warnings.slice(0, 8)) {
        body.append(h('div', { class: 'note warn' }, `${file.name}: ${w.message}`));
      }
      if (board.warnings.length > 8) {
        body.append(h('div', { class: 'note' }, `… and ${board.warnings.length - 8} more parser warnings.`));
      }
    }

    body.append(
      h(
        'div',
        { class: 'note' },
        'The map draws bounding boxes, not copper. Anything magenta is something the model could not measure ' +
          'exactly — it is shown at its position rather than omitted.',
      ),
    );

    return this.panel('coverage', 'Coverage', flagged > 0 ? `${flagged} flagged` : 'ok', body);
  }

  // --- right column (merge) ---------------------------------------------------------

  private renderRight(): void {
    delete this.els.mergeButton;
    const col = clear(this.els.right);

    // Browsing needs no right-hand column at all; the canvas gets the width.
    if (!this.merging) {
      this.root.classList.remove('merging', 'right-collapsed');
      return;
    }
    this.root.classList.add('merging');
    this.root.classList.toggle('right-collapsed', this.rightCollapsed);

    if (this.rightCollapsed) {
      col.append(
        this.rail('right', [
          h(
            'span',
            { class: 'rail-badge merging', title: plural(this.selections.length, 'selected area') },
            String(this.selections.length),
          ),
        ]),
      );
      return;
    }

    // Repeated where the user is actually looking while merging. One exit in a
    // crowded toolbar reads as part of the view controls beside it.
    col.append(
      h(
        'div',
        { class: 'merge-banner pinned-top' },
        h('span', {}, 'Choosing what to merge'),
        h(
          'button',
          { class: 'sm exit-merge', title: 'Leave merge mode (Esc)', onclick: () => this.stopMerging() },
          '× Exit',
        ),
        this.collapseButton('right'),
      ),
    );
    col.append(this.selectionsPanel());
    col.append(this.mergePanel());
    this.stackHeaders(col);
  }

  /**
   * The rectangles the user has drawn, each one a statement of intent.
   *
   * Named, deletable and expandable: a list of intents you can review and argue
   * about beats a mass of ticked boxes, because it records *why*.
   */
  private selectionsPanel(): HTMLElement {
    const body = h('div', { class: 'panel-body' });
    const diff = this.diff;

    if (this.selections.length === 0) {
      body.append(
        h('div', { class: 'empty' }, 'Drag a rectangle on the board to choose what to merge.'),
        h(
          'div',
          { class: 'note' },
          'Shift-drag pans instead of drawing. Overlapping rectangles are combined, not counted twice.',
        ),
      );
      return this.panel('selections', 'Selections', undefined, body);
    }

    for (const [index, selection] of this.selections.entries()) {
      body.append(this.selectionRow(selection, index, diff));
    }

    return this.panel('selections', 'Selections', plural(this.selections.length, 'area'), body);
  }

  private selectionRow(selection: Selection, index: number, diff?: DiffResult): HTMLElement {
    const contents = diff ? selectionContents(diff, selection) : undefined;
    const folded = this.collapsedSelections.has(selection.id);
    const colour = selectionColor(index);
    const active = selection.id === this.activeSelectionId;

    const swatch = h('i', { class: 'swatch' });
    swatch.style.background = colour;

    const name = h('input', {
      type: 'text',
      class: 'selection-name',
      value: selection.name,
      onchange: (ev: Event) => {
        selection.name = (ev.target as HTMLInputElement).value || selection.name;
        this.render();
      },
    });

    const head = h(
      'div',
      { class: active ? 'selection-head active' : 'selection-head' },
      h(
        'button',
        {
          class: 'chev',
          title: folded ? 'Show what this catches' : 'Hide the detail',
          onclick: () => {
            if (folded) this.collapsedSelections.delete(selection.id);
            else this.collapsedSelections.add(selection.id);
            this.render();
          },
        },
        chevron(folded),
      ),
      swatch,
      name,
      h(
        'button',
        {
          class: 'sm',
          title: 'Zoom to this rectangle',
          onclick: () => {
            this.activeSelectionId = selection.id;
            this.view?.zoomTo(selection.rect);
            this.render();
          },
        },
        'Show',
      ),
      h(
        'button',
        {
          class: 'sm',
          title: 'Delete this selection',
          onclick: () => {
            this.selections = this.selections.filter((s) => s.id !== selection.id);
            if (this.activeSelectionId === selection.id) this.activeSelectionId = undefined;
            this.rebuildPlan();
            this.render();
          },
        },
        '×',
      ),
    );

    const wrap = h('div', { class: 'selection' }, head);

    if (contents === undefined) return wrap;

    wrap.append(
      h(
        'div',
        { class: 'selection-summary' },
        `${plural(contents.taken.length, 'change')} taken` +
          (contents.undecided.length > 0 ? `, ${contents.undecided.length} across the edge` : ''),
      ),
    );

    if (folded) return wrap;

    // Straddlers first: they are the question the rectangle could not answer.
    if (contents.undecided.length > 0) {
      const straddle = h('div', { class: 'straddlers' });
      straddle.append(
        h(
          'div',
          { class: 'note warn' },
          `${plural(contents.undecided.length, 'change')} hang over the edge of this rectangle, or ` +
            `cannot be measured. They are left out until you say otherwise.`,
        ),
        h(
          'button',
          {
            class: 'sm',
            onclick: () => {
              selection.included = [...new Set([...selection.included, ...contents.undecided.map((c) => c.key)])];
              this.rebuildPlan();
              this.render();
            },
          },
          'Include them all',
        ),
      );
      wrap.append(straddle);
    }

    const list = h('ul', { class: 'selection-changes' });
    for (const caught of contents.caught.slice(0, 200)) {
      const change = caught.change;
      list.append(
        h(
          'li',
          { class: change.key === this.selectedKey ? 'sel' : '' },
          h('input', {
            type: 'checkbox',
            checked: caught.taken,
            title: caught.containment === 'inside' ? 'Inside the rectangle' : 'Across its edge, or unmeasurable',
            onchange: () => this.toggleInSelection(selection, caught.change.key, caught.containment !== 'inside'),
          }),
          h(
            'span',
            {
              class: 'what',
              onclick: () => this.select(change),
            },
            describeChange(change, (o) => Board.describe(o)),
          ),
        ),
      );
    }
    if (contents.caught.length > 200) {
      list.append(h('li', { class: 'note' }, `… and ${contents.caught.length - 200} more`));
    }
    wrap.append(list);

    // Per-layer breakdown: a rectangle reaches onto layers that may be hidden,
    // and silently taking those is exactly the surprise this tool exists to stop.
    const layers = new Map<string, number>();
    for (const change of contents.taken) {
      for (const layer of (change.b ?? change.a)!.layers) layers.set(layer, (layers.get(layer) ?? 0) + 1);
    }
    if (layers.size > 0) {
      // Only meaningful when there is a view to hide things in.
      const hidden = this.view === undefined ? [] : [...layers.keys()].filter((l) => !this.visibleLayers.has(l));
      const line = h(
        'div',
        { class: hidden.length > 0 ? 'note warn' : 'note' },
        [...layers]
          .sort(([, x], [, y]) => y - x)
          .map(([l, n]) => `${layerLabel(l)} ${n}`)
          .join(' · '),
      );
      wrap.append(line);
      if (hidden.length > 0) {
        wrap.append(
          h(
            'div',
            { class: 'note warn' },
            `${plural(hidden.length, 'layer')} you have hidden: ${hidden.map(layerLabel).join(', ')}.`,
          ),
        );
      }
    }

    return wrap;
  }

  private toggleInSelection(selection: Selection, key: string, straddling: boolean): void {
    if (straddling) {
      selection.included = selection.included.includes(key)
        ? selection.included.filter((k) => k !== key)
        : [...selection.included, key];
    } else {
      selection.excluded = selection.excluded.includes(key)
        ? selection.excluded.filter((k) => k !== key)
        : [...selection.excluded, key];
    }
    this.rebuildPlan();
    this.render();
  }

  /**
   * What the merge will do, and the gate in front of it.
   */
  private mergePanel(): HTMLElement {
    const body = h('div', { class: 'panel-body' });
    const check = this.planCheck;

    if (!check || !this.a?.board || !this.b?.board) {
      body.append(h('div', { class: 'empty' }, 'Load both boards to enable merging.'));
      return this.panel('merge', 'Result', undefined, body);
    }

    // Board-level differences have no position on the canvas, so they cannot be
    // caught by a rectangle. They are chosen from a list instead.
    const resolvable = this.facts.filter((f) => !f.same && f.resolvableBy !== undefined);
    if (resolvable.length > 0) {
      const globals = h('div', { class: 'globals' });
      globals.append(h('div', { class: 'note' }, 'These belong to the whole board, so they are taken or not as a unit:'));
      for (const fact of resolvable) {
        const id = fact.resolvableBy!;
        globals.append(
          h(
            'label',
            { class: 'checkbox', title: GLOBAL_BLOCK_INFO[id].help },
            h('input', {
              type: 'checkbox',
              checked: this.takeGlobal.has(id),
              onchange: (ev: Event) => {
                if ((ev.target as HTMLInputElement).checked) this.takeGlobal.add(id);
                else this.takeGlobal.delete(id);
                this.rebuildPlan();
                this.render();
              },
            }),
            `Take Board B's ${GLOBAL_BLOCK_INFO[id].label.toLowerCase()}`,
          ),
        );
      }
      body.append(globals);
    }

    // What happens to Board A, in the vocabulary of the board.
    const lines = describeEffects(check.effects, this.a.name);
    const effects = h('div', { class: 'effects' });
    for (const line of lines) effects.append(h('div', { class: `effect ${line.tone}` }, line.text));
    body.append(effects);

    for (const issue of check.issues) {
      if (issue.level === 'note') continue;
      body.append(
        h(
          'div',
          { class: `issue ${issue.level}` },
          h('div', { class: 'vt' }, issue.title),
          h('div', { class: 'vd' }, issue.detail),
        ),
      );
    }

    if (check.blockers.length > 0) {
      body.append(
        h(
          'div',
          { class: 'banner bad' },
          'Merging is disabled. These would produce a file KiCad may refuse to load — change the selection, ' +
            'or take the board-level state that would make them valid.',
        ),
      );
    } else if (check.confirmations.length > 0) {
      body.append(
        h(
          'label',
          { class: 'checkbox confirm-ack' },
          h('input', {
            type: 'checkbox',
            checked: this.overrideAcknowledged,
            onchange: (ev: Event) => {
              this.overrideAcknowledged = (ev.target as HTMLInputElement).checked;
              this.updateMergeGate();
            },
          }),
          `I have read the ${plural(check.confirmations.length, 'item')} above and accept them.`,
        ),
        h(
          'label',
          {},
          `Type "${OVERRIDE_CONFIRMATION}" to confirm`,
          h('input', {
            type: 'text',
            value: this.overrideText,
            placeholder: OVERRIDE_CONFIRMATION,
            // Deliberately *not* a re-render. Rebuilding this column would
            // destroy the field being typed into and take the caret with it,
            // so the phrase could never be typed. `onchange` is no use either:
            // it fires on blur, and the button has to unlock as the last
            // character lands. So the state is updated and only the button's
            // enabled-ness is touched.
            oninput: (ev: Event) => {
              this.overrideText = (ev.target as HTMLInputElement).value;
              this.updateMergeGate();
            },
          }),
        ),
      );
    }

    const mergeButton = h(
      'button',
      {
        class: check.confirmations.length > 0 ? 'danger' : 'primary',
        disabled: !this.canMerge(),
        onclick: () => void this.runMergeAsync(),
      },
      'Build merged file',
    );
    this.els.mergeButton = mergeButton;
    body.append(mergeButton);

    if (this.mergeError) {
      body.append(
        h('div', { class: 'banner bad' }, this.mergeError.message),
        h('pre', { class: 'report' }, this.mergeError.reasons.join('\n')),
      );
    }

    if (this.mergeResult) {
      const result = this.mergeResult;
      body.append(
        h(
          'div',
          { class: 'banner warn' },
          result.needsZoneRefill
            ? 'Merged. Open the file in KiCad and run Edit → Fill All Zones before plotting or ordering.'
            : 'Merged.',
        ),
        h(
          'div',
          { class: 'row' },
          h(
            'button',
            { class: 'primary', onclick: () => this.saveMergedFile() },
            `Save ${result.fileName}`,
          ),
          h('button', { class: 'sm', onclick: () => void navigator.clipboard?.writeText(result.report) }, 'Copy report'),
        ),
      );
      if (this.saveNotice) body.append(h('div', { class: 'note' }, this.saveNotice));
      body.append(h('pre', { class: 'report' }, result.report));
    }

    return this.panel('merge', 'Result', undefined, body);
  }

  // --- merge mode ---------------------------------------------------------------

  /**
   * Enter merge mode.
   *
   * Public because it is a mode of the application rather than a detail of the
   * button that happens to trigger it — the same is true of {@link selectArea}.
   */
  beginMerge(): void {
    this.startMerging();
  }

  /** Claim an area of the board, as dragging a rectangle on it does. */
  selectArea(rect: Rect, name?: string): Selection {
    const selection = makeSelection(`sel-${++this.selectionCounter}`, name ?? `Selection ${this.selectionCounter}`, rect);
    this.selections = [...this.selections, selection];
    this.activeSelectionId = selection.id;
    this.rebuildPlan();
    this.render();
    return selection;
  }

  private startMerging(): void {
    this.merging = true;
    this.mergeResult = undefined;
    this.mergeError = undefined;
    this.rebuildPlan();
    this.render();
  }

  private stopMerging(): void {
    this.merging = false;
    this.render();
  }

  /** Add a rectangle the user dragged out on the board. */
  private addSelection(rect: Rect): void {
    this.selectArea(rect);
  }

  /** Re-derive the plan and its checks from the current selections. */
  private rebuildPlan(): void {
    const a = this.a?.board;
    const b = this.b?.board;
    const diff = this.diff;
    if (!a || !b || !diff) {
      this.plan = undefined;
      this.planCheck = undefined;
      return;
    }
    this.plan = buildPlan(diff, this.selections, [...this.takeGlobal]);
    this.planCheck = checkPlan(a, b, diff, this.plan);
  }

  /** Whether the merge may run. */
  private canMerge(): boolean {
    const check = this.planCheck;
    if (!check?.canMerge) return false;
    if (check.confirmations.length === 0) return true;
    return this.overrideAcknowledged && this.overrideText.trim() === OVERRIDE_CONFIRMATION;
  }

  /**
   * Re-evaluate the merge gate without rebuilding anything.
   *
   * The confirmation controls are the only inputs whose value changes what the
   * merge button does, and they are the ones the user is actively typing into.
   */
  private updateMergeGate(): void {
    const button = this.els.mergeButton;
    if (button) button.disabled = !this.canMerge();
  }

  private async runMergeAsync(): Promise<void> {
    await this.busyRun('Merging and re-checking the result…', () => this.runMerge());
    if (this.mergeResult) {
      await this.busyRun('Loading the merged board…', () => this.adoptMerge());
    }
    this.render();
  }

  /**
   * Make the merged file Board A.
   *
   * The result used to sit in memory until it was downloaded, which meant the
   * one thing the user wanted to check — what the merged board actually looks
   * like — was the one thing they could not do without leaving the tool. So it
   * becomes Board A, the comparison re-runs against it, and what is left
   * unmerged is immediately visible.
   *
   * Nothing is written to disk. The notice over the canvas says so, and Revert
   * puts the original back.
   */
  private adoptMerge(): void {
    const result = this.mergeResult;
    if (!result) return;

    this.originalA ??= this.a;
    this.parseInto('a', result.fileName, result.text);
    // The change keys are all new, so any per-change corrections the user made
    // no longer refer to anything. The rectangles are still what they meant.
    for (const selection of this.selections) {
      selection.excluded = [];
      selection.included = [];
    }

    // The rectangles described differences that no longer exist — they were just
    // merged. Leaving them in the list as empty selections invites the reader to
    // think something is still pending.
    this.selections = [];
    this.activeSelectionId = undefined;
    this.collapsedSelections = new Set();
    this.takeGlobal = new Set();
    this.overrideAcknowledged = false;
    this.overrideText = '';

    // Re-comparing clears the last merge, which is right when the boards change
    // underneath it and wrong here: this *is* that merge, and the file it
    // produced still has to be saved to disk. Nothing else has changed, so the
    // result is carried across the recomputation.
    this.recomputeCore();
    this.mergeResult = result;
  }

  /**
   * Write the merged file out.
   *
   * Shared, because the file has to be reachable from wherever the user happens
   * to be: merging is a mode they leave, and the result outlives it.
   */
  private saveMergedFile(): void {
    const result = this.mergeResult;
    if (!result) return;
    void saveFile(result.fileName, result.text)
      .then((how) => {
        this.saveNotice = how === 'saved' ? `Written to ${result.fileName}.` : `Downloaded ${result.fileName}.`;
        this.render();
      })
      .catch(() => {
        this.saveNotice = 'Save cancelled.';
        this.render();
      });
  }

  /**
   * The Save button for a merged Board A, wherever it needs to appear.
   *
   * A result exists exactly while Board A is one — reverting discards both — so
   * the presence of the result is the whole condition.
   */
  private saveMergedButton(className = 'primary'): HTMLElement | null {
    const result = this.mergeResult;
    if (result === undefined) return null;
    return h(
      'button',
      { class: className, title: `Write ${result.fileName}`, onclick: () => this.saveMergedFile() },
      `Save ${result.fileName}`,
    );
  }

  /** Put the file the user opened back, discarding the merged board. */
  private revertMerge(): void {
    const original = this.originalA;
    if (!original) return;
    this.a = original;
    this.originalA = undefined;
    this.mergeResult = undefined;
    this.mergeError = undefined;
    this.saveNotice = undefined;
    this.overrideAcknowledged = false;
    this.overrideText = '';
    for (const selection of this.selections) {
      selection.excluded = [];
      selection.included = [];
    }
    this.recompute();
  }

  /** Whether Board A is currently a merge result rather than the file opened. */
  private get boardAIsMerged(): boolean {
    return this.originalA !== undefined;
  }

  private runMerge(): void {
    const a = this.a?.board;
    const b = this.b?.board;
    const diff = this.diff;
    const built = this.plan;
    if (!a || !b || !diff || !built) return;

    this.mergeError = undefined;
    this.mergeResult = undefined;
    this.saveNotice = undefined;
    try {
      this.mergeResult = merge(a, b, {
        built,
        diff,
        ...(this.planCheck ? { check: this.planCheck } : {}),
        confirm: { acknowledged: this.overrideAcknowledged, confirmation: this.overrideText },
      });
    } catch (err) {
      if (err instanceof MergeAbort) {
        this.mergeError = err;
      } else {
        this.mergeError = new MergeAbort(
          'The merge failed with an unexpected internal error. No file was produced.',
          [(err as Error).message],
        );
      }
    }
  }

  // --- keyboard (spec §6.8) -----------------------------------------------------

  private bindKeys(): void {
    globalThis.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.els.dialog) {
        ev.preventDefault();
        this.closeDialog();
        return;
      }
      // Escape is the universal "get me out of this mode". It leaves the
      // selections alone — re-entering picks up where you left off.
      if (ev.key === 'Escape' && this.merging) {
        ev.preventDefault();
        this.stopMerging();
        return;
      }
      const target = ev.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (this.els.dialog) return;
      const changes = this.diff?.substantive ?? [];

      switch (ev.key) {
        case 'ArrowRight':
        case 'ArrowLeft': {
          if (changes.length === 0) return;
          ev.preventDefault();
          const current = changes.findIndex((c) => c.key === this.selectedKey);
          const step = ev.key === 'ArrowRight' ? 1 : -1;
          const next = current < 0 ? (step > 0 ? 0 : changes.length - 1) : (current + step + changes.length) % changes.length;
          this.select(changes[next]!);
          return;
        }
        case 'f':
          ev.preventDefault();
          this.view?.fit();
          return;
        case '1':
        case '2':
        case '3':
        case '4':
          ev.preventDefault();
          this.mode = ({ '1': 'a', '2': 'b', '3': 'onion', '4': 'split' } as const)[ev.key];
          this.render();
          return;
        default:
          return;
      }
    });
  }
}

// --- small helpers ----------------------------------------------------------------



/**
 * Name a board fetched from git so its revision is visible everywhere the file
 * name appears — the change list, the verification report, the merge report and
 * the default output name. Kept ending in `.kicad_pcb` so the output name still
 * comes out sensibly.
 */
function repoFileName(path: string, ref: string): string {
  const base = (path.split('/').pop() ?? path).replace(/\.kicad_pcb$/i, '');
  const short = /^[0-9a-f]{40}$/i.test(ref) ? ref.slice(0, 8) : ref.replace(/[\\/]/g, '-');
  return `${base}@${short}.kicad_pcb`;
}

/**
 * An on/off view option.
 *
 * Carries a real checkbox rather than relying on the button's fill alone —
 * a highlighted button reads as "press me" just as easily as "this is on".
 */
function toggle(label: string, on: boolean, onChange: (on: boolean) => void): HTMLElement {
  return h(
    'button',
    {
      class: on ? 'sm toggle on' : 'sm toggle',
      'aria-pressed': String(on),
      title: `${label}: ${on ? 'on' : 'off'}`,
      onclick: () => onChange(!on),
    },
    h('i', { class: 'box' }),
    label,
  );
}

/** Fold/unfold marker for a panel header. */
function chevron(folded: boolean): SVGElement {
  return svgIcon(folded ? 'M6 3l5 5-5 5' : 'M3 6l5 5 5-5', 11);
}

function legendItem(color: string, label: string): HTMLElement {
  const swatch = h('i', {});
  swatch.style.borderColor = color;
  swatch.style.background = `${color}33`;
  return h('span', {}, swatch, label);
}

/**
 * A one-line summary of *what* changed, for the change list.
 *
 * Nets first: on a real comparison most modifications are net reassignments on
 * pads, and those are the ones that matter most to see.
 */
function summariseChange(change: ObjectChange): HTMLElement | null {
  const parts: string[] = [];

  if (change.displacement) {
    const [dx, dy] = change.displacement;
    parts.push(`moved ${mm(Math.hypot(dx, dy), 2)} mm`);
  }
  if (change.rotation) parts.push(`rotated ${change.rotation[0]}° → ${change.rotation[1]}°`);

  const fields = change.fields;
  const nets = fields.filter((f) => /(^|\.)net(\[|$|\.)/.test(f.path));
  const others = fields.filter((f) => !nets.includes(f));
  for (const f of [...nets, ...others].slice(0, 2)) {
    parts.push(shortFieldChange(f));
  }
  const shown = Math.min(fields.length, 2);
  if (fields.length > shown) parts.push(`+${fields.length - shown} more`);

  if (parts.length === 0) return null;
  return h('span', { class: 'detail' }, parts.join(' · '));
}

/** `net: OLD → NEW`, trimmed to fit a narrow panel. */
function shortFieldChange(f: FieldChange): string {
  const name = f.path.split('.').pop() ?? f.path;
  const clip = (v: string | undefined): string => {
    if (v === undefined) return '—';
    const t = v.replace(/\s+/g, ' ').trim();
    return t.length > 28 ? `${t.slice(0, 27)}…` : t;
  };
  if (f.before === undefined) return `${name} +${clip(f.after)}`;
  if (f.after === undefined) return `${name} −${clip(f.before)}`;
  return `${name}: ${clip(f.before)} → ${clip(f.after)}`;
}

function glyphFor(kind: ObjectChange['kind']): string {
  switch (kind) {
    case 'added':
      return '+';
    case 'removed':
      return '−';
    case 'moved':
      return '→';
    case 'modified':
      return '~';
    default:
      return '=';
  }
}
